import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBase from 'LuxKit/Modal/ModalBase'
import config from 'constants/config'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import BodyText from 'LuxKit/Typography/BodyText'
import Table from 'LuxKit/Tables/Table'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import Pill from 'LuxKit/Pill'
import styled from 'styled-components'
import { rem } from 'polished'
import { getImageUrl } from 'lib/image/imageUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

const SPEND_EARN_MODAL_IMAGE_BACKGROUND = 'promo-spend-earn-background'

const SpendEarnHeader = styled(VerticalSpacer)`
  background-image: url(${getImageUrl(SPEND_EARN_MODAL_IMAGE_BACKGROUND, { brightness: -50 })});
  background-position: center;
  background-size: 100%;
  padding: ${rem(48)} 0;
`

interface Props {
  maxCredit: number
  spendEarnList: Array<{ spend: number, earn: number }>
}

function TourSpendEarnDetailModal({ maxCredit, spendEarnList }: Props) {
  const currency = useContext(GeoContext).currentCurrency
  const elementContext = useModalElementContext()
  const intl = useIntl()

  const onClose = useCallback(() => elementContext?.resolve(), [elementContext])

  return <ModalBase>
    <ModalHeader
      title={intl.formatMessage(
        {
          id: 'VPsW68Hsqb3-6V8dBORF',
          defaultMessage: 'Spend more, earn more',
        },
      )}
      isOverlay
    />
    <ModalBody>
      <SpendEarnHeader gap={16}>
        <HeadingTextBlock colour="dark-contrast" variant="heading1" align="center">
          <FormattedMessage
            id="gKzCt3uqiGblbL0CW2Bm"
            defaultMessage="<i>Spend</i> more,<br></br><i>Earn</i> more"
            
            values={{
              i: (chunks) => <i>{chunks}</i>,
              br: () => <br />,
            }}
          />
        </HeadingTextBlock>
        <HeadingTextBlock colour="dark-contrast" variant="heading6" align="center">
          <FormattedMessage
            id="iwkL7H-L11mbD4iiCYMN"
            defaultMessage="Use code <pill>TOURDEAL</pill> at checkout"
            
            values={{
              pill: (chunks) => <Pill palette="success">{chunks}</Pill>,
            }}
          />
        </HeadingTextBlock>
      </SpendEarnHeader>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <HeadingTextBlock variant="heading4">
            <FormattedMessage
              id="rXt-n_IPpt7HCKL-_iXp"
              defaultMessage="Book a tour and enjoy up to {amount} {brandName} credit"
              
              values={{
                amount: <FormatCurrency value={maxCredit} currency={currency} />,
                brandName: config.brandConfig.name,
              }}
            />
          </HeadingTextBlock>
          <Table>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage(
                      {
                        id: '1KkgfMNmeJmaFRHTnkji',
                        defaultMessage: 'Total spend',
                      },
                    )}
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage(
                      {
                        id: 'u_Xw8AcffYFnzJfpALpy',
                        defaultMessage: 'Credit you’ll earn',
                      },
                    )}
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
              {spendEarnList.map(({ spend, earn }) => <TableBodyRow key={`${spend}-${earn}`}>
                <TableBodyCell><FormatCurrency value={spend} currency={currency} /></TableBodyCell>
                <TableBodyCell><FormatCurrency value={earn} currency={currency} /></TableBodyCell>
              </TableBodyRow>,
              )}
            </TableBody>
          </Table>
          <BodyTextBlock variant="medium">
            <FormattedMessage
              id="D5UxaX7EdW8ZRkJpXdaz"
              defaultMessage="Offer applies to {brandName} Tours only. Code can only be used once per customer. <link>Terms & conditions apply.</link>"
              
              values={{
                brandName: config.brandConfig.name,
                link: (chunks) => <TextLink onClick={onClose} to="/promotions" weight="regular">{chunks}</TextLink>,
              }}
            />
          </BodyTextBlock>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default TourSpendEarnDetailModal
